import React, { useRef, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { searchApiService } from '../services/searchApiService'
import { useDebounce } from '../hooks/useDebounce'
import { StatusChip, VersionChip, RoleChip, UserStatusChip } from './ui/Chip'
import { Avatar } from './ui/Avatar'
import { colors, fontFamily, fontSize, radius, shadow, eyebrow as eyebrowStyle } from '../styles/theme'
import type {
  GlobalSearchResult, SearchTemplateHit, SearchFolderHit, SearchUserHit,
} from '../types'

// ── Section labels ────────────────────────────────────────────────────────
const SECTION_LABEL: React.CSSProperties = {
  ...eyebrowStyle,
  padding: '8px 14px 4px',
}

const RESULT_ROW_BASE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '9px 14px',
  cursor: 'pointer',
  transition: 'background 120ms ease',
  textDecoration: 'none',
}

function ResultRow({
  onClick,
  children,
}: {
  onClick: () => void
  children: React.ReactNode
}) {
  const [hovered, setHovered] = React.useState(false)
  return (
    <div
      role="option"
      tabIndex={-1}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...RESULT_ROW_BASE,
        background: hovered ? colors.surfaceTinted ?? '#F4FAF9' : 'transparent',
      }}
    >
      {children}
    </div>
  )
}

// ── Icons ─────────────────────────────────────────────────────────────────
function Ic({ path, size = 14 }: { path: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={colors.textMuted} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }}>
      <path d={path} />
    </svg>
  )
}

const IC_TEMPLATE = 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8ZM14 2v6h6M16 13H8M16 17H8'
const IC_FOLDER   = 'M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z'
const IC_USER     = 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'
const IC_SEARCH   = 'M21 21l-6-6m2-5a7 7 0 1 1-14 0 7 7 0 0 1 14 0z'

// ── Main component ─────────────────────────────────────────────────────────
export function GlobalSearch() {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<GlobalSearchResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const debouncedQuery = useDebounce(query, 280)
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Fetch results
  useEffect(() => {
    const q = debouncedQuery.trim()
    if (q.length < 1) {
      setResults(null)
      return
    }
    let cancelled = false
    setLoading(true)
    searchApiService.search(q)
      .then(r => { if (!cancelled) { setResults(r); setOpen(true) } })
      .catch(() => { if (!cancelled) setResults(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [debouncedQuery])

  // Close on outside click
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  function close() { setOpen(false); setQuery('') }
  function go(path: string) { navigate(path); close() }

  const hasResults = results && (
    results.templates.length + results.folders.length + results.users.length > 0
  )

  return (
    <div ref={containerRef} style={{ position: 'relative', flex: 1, maxWidth: 400 }}>
      {/* Search input */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
          <Ic path={IC_SEARCH} size={13} />
        </span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => { if (results) setOpen(true) }}
          placeholder="Search templates, folders, users…"
          style={{
            width: '100%',
            height: 34,
            padding: '0 32px 0 32px',
            fontFamily: fontFamily.sans,
            fontSize: fontSize.sm,
            color: colors.text,
            background: colors.background,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.full,
            outline: 'none',
            letterSpacing: '-0.01em',
          }}
          onKeyDown={e => { if (e.key === 'Escape') close() }}
        />
        {loading && (
          <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
            <div style={{ width: 12, height: 12, border: `2px solid ${colors.border}`, borderTopColor: colors.primary, borderRadius: '50%', animation: 'docm-spin 0.7s linear infinite' }} />
          </span>
        )}
      </div>

      {/* Results popover */}
      {open && query.trim().length > 0 && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          left: 0,
          right: 0,
          minWidth: 480,
          maxWidth: 600,
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: radius.lg,
          boxShadow: shadow.lg,
          zIndex: 500,
          overflow: 'hidden',
          animation: 'docm-slide-in-up 120ms ease',
        }}
          role="listbox"
        >
          {!hasResults && !loading && (
            <div style={{ padding: '24px 14px', textAlign: 'center', fontSize: fontSize.sm, color: colors.textMuted }}>
              No results for "{query.trim()}"
            </div>
          )}

          {results?.templates && results.templates.length > 0 && (
            <Section label="Templates">
              {results.templates.map(t => (
                <TemplateResult key={t.templateId} hit={t} onClick={() => go(`/app/templates/${t.templateId}`)} />
              ))}
            </Section>
          )}

          {results?.folders && results.folders.length > 0 && (
            <Section label="Folders">
              {results.folders.map(f => (
                <FolderResult key={f.folderId} hit={f} onClick={() => go(`/app/templates?folderId=${f.folderId}`)} />
              ))}
            </Section>
          )}


          {results?.users && results.users.length > 0 && (
            <Section label="Users">
              {results.users.map(u => (
                <UserResult key={u.userId} hit={u} onClick={() => go(`/app/admin/users`)} />
              ))}
            </Section>
          )}
        </div>
      )}
    </div>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ borderBottom: `1px solid ${colors.border}` }}>
      <div style={SECTION_LABEL}>{label}</div>
      {children}
    </div>
  )
}

function TemplateResult({ hit, onClick }: { hit: SearchTemplateHit; onClick: () => void }) {
  return (
    <ResultRow onClick={onClick}>
      <Ic path={IC_TEMPLATE} />
      <span style={{ flex: 1, minWidth: 0, fontSize: fontSize.sm, color: colors.text, letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {hit.name}
      </span>
      <StatusChip status={hit.status} />
      {hit.version != null && <VersionChip version={hit.version} active={hit.status === 'Active'} />}
      <span style={{ fontSize: fontSize.xs, color: colors.textMuted, whiteSpace: 'nowrap', minWidth: 80, textAlign: 'right' }}>
        {fmtDate(hit.updatedAt)}
      </span>
    </ResultRow>
  )
}

function FolderResult({ hit, onClick }: { hit: SearchFolderHit; onClick: () => void }) {
  return (
    <ResultRow onClick={onClick}>
      <Ic path={IC_FOLDER} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: fontSize.sm, color: colors.text, letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {hit.name}
        </div>
        <div style={{ fontSize: fontSize.xs, color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: fontFamily.mono }}>
          {hit.path}
        </div>
      </span>
      <span style={{ fontSize: fontSize.xs, color: colors.textMuted, whiteSpace: 'nowrap' }}>
        {fmtDate(hit.updatedAt)}
      </span>
    </ResultRow>
  )
}


function UserResult({ hit, onClick }: { hit: SearchUserHit; onClick: () => void }) {
  const displayName = hit.fullName ?? hit.email
  return (
    <ResultRow onClick={onClick}>
      <Avatar name={displayName} size={24} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: fontSize.sm, color: colors.text, letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayName}
        </div>
        <div style={{ fontSize: fontSize.xs, color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {hit.email}
        </div>
      </span>
      <RoleChip role={hit.role} />
      <UserStatusChip status={hit.status} />
    </ResultRow>
  )
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffH = diffMs / 3600000
    if (diffH < 1) return `${Math.round(diffMs / 60000)}m ago`
    if (diffH < 24) return `${Math.round(diffH)}h ago`
    const diffD = Math.round(diffH / 24)
    if (diffD < 7) return `${diffD}d ago`
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  } catch { return '' }
}
